import { useEffect, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import AddAbbreviationRow from './AddAbbreviationRow'
import AbbreviationRow from './AbbreviationRow'
import { useAbbreviationsCache, useSettingsCache, type CachedAbbreviation } from '@/shared/cache'
import { smartSearch } from '@/shared/utils/smartSearch'
import { toast } from 'sonner'

export default function AbbreviationsTab() {
	const {
		enabled,
		abbreviations,
		setLocalEnabled,
		createAbbreviation,
		updateAbbreviation,
		deleteAbbreviation,
	} = useAbbreviationsCache()
	const { updateSettings } = useSettingsCache()

	const [searchQuery, setSearchQuery] = useState('')
	const [highlightId, setHighlightId] = useState<number | null>(null)
	const [pendingHighlightAbbreviation, setPendingHighlightAbbreviation] = useState<string | null>(null)

	// Resolve pending highlight after React re-renders with the new abbreviation
	useEffect(() => {
		if (!pendingHighlightAbbreviation) return
		const found = abbreviations.find(a => a.abbreviation === pendingHighlightAbbreviation)
		if (found) {
			setHighlightId(found.id)
			setPendingHighlightAbbreviation(null)
		}
	}, [abbreviations, pendingHighlightAbbreviation])

	// Clear highlight after animation
	useEffect(() => {
		if (highlightId === null) return
		const timer = setTimeout(() => setHighlightId(null), 1500)
		return () => clearTimeout(timer)
	}, [highlightId])

	const searchFields = useMemo(() => [
		(a: CachedAbbreviation) => ({ value: a.abbreviation ?? '', weight: 1.5 }),
		(a: CachedAbbreviation) => ({ value: a.expansion ?? '' }),
	], [])

	const sortFn = useMemo(
		() => (a: CachedAbbreviation, b: CachedAbbreviation) =>
			(a.abbreviation ?? '').localeCompare(b.abbreviation ?? ''),
		[],
	)

	const filtered = useMemo(() => {
		const sorted = [...abbreviations].sort(sortFn)
		if (!searchQuery.trim()) return sorted
		return smartSearch(sorted, searchQuery, searchFields, { limit: sorted.length }).map((r) => r.item)
	}, [abbreviations, searchQuery, searchFields, sortFn])

	const handleAdd = async (abbreviation: string, expansion: string) => {
		const result = await createAbbreviation(abbreviation, expansion)
		if (!result.ok) {
			toast.error(result.error)
		} else {
			toast.success('Abbreviation added')
			setPendingHighlightAbbreviation(abbreviation)
		}
	}

	const handleUpdate = async (id: number, fields: { abbreviation?: string; expansion?: string }) => {
		const result = await updateAbbreviation(id, fields)
		if (!result.ok) toast.error(result.error)
	}

	const handleDelete = async (id: number) => {
		const deletedItem = abbreviations.find(a => a.id === id)
		if (!deletedItem) return

		const result = await deleteAbbreviation(id)
		if (!result.ok) {
			toast.error(result.error)
			return
		}
		toast('Abbreviation deleted', {
			description: `"${deletedItem.abbreviation}" removed`,
			action: {
				label: 'Undo',
				onClick: async () => {
					const undoResult = await createAbbreviation(deletedItem.abbreviation, deletedItem.expansion)
					if (!undoResult.ok) toast.error(undoResult.error)
				},
			},
			duration: 5000,
		})
	}

	const handleToggle = async (value: boolean) => {
		const rollback = setLocalEnabled(value)
		try {
			await updateSettings({ abbreviations_enabled: value })
		} catch {
			rollback()
		}
	}

	return (
		<div className="flex flex-col h-full min-h-0">
			{/* Enable toggle */}
			<div className="flex items-center justify-between py-4 border-b border-border/40">
				<div className="flex flex-col gap-1 pr-4">
					<Label className="text-sm font-medium font-sans text-foreground">
						Enable abbreviations
					</Label>
					<p className="text-xs text-muted-foreground max-w-[400px] leading-relaxed">
						When enabled, typing an abbreviation followed by a space will automatically expand it to the full text.
					</p>
				</div>
				<div className="shrink-0">
					<Switch checked={enabled} onCheckedChange={handleToggle} />
				</div>
			</div>

			{/* Disabled banner */}
			{!enabled && (
				<div className="rounded-md border border-border/50 bg-muted/40 px-4 py-3 mt-4">
					<p className="text-sm text-muted-foreground">
						Abbreviation expansion is currently off. Your abbreviations are saved but
						won't auto-expand until you re-enable.
					</p>
				</div>
			)}

			{/* Sticky: add row & search */}
			<div className={`sticky top-0 z-10 bg-background flex flex-col gap-4 py-4 transition-opacity ${!enabled ? 'opacity-60' : ''}`}>
				<AddAbbreviationRow
					onAdd={handleAdd}
					existingAbbreviations={abbreviations.map((a) => a.abbreviation)}
					count={abbreviations.length}
				/>

				{abbreviations.length > 0 && (
					<div className="flex items-center gap-2">
						<div className="relative flex-1">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
							<Input
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search abbreviations..."
								className="pl-8 h-9 text-sm"
							/>
						</div>
						<span className="text-xs text-muted-foreground shrink-0 tabular-nums">
							{filtered.length} / {abbreviations.length}
						</span>
					</div>
				)}
			</div>

			{/* Abbreviation list */}
			<div className={`flex-1 min-h-0 transition-opacity ${!enabled ? 'opacity-60' : ''}`}>
				{abbreviations.length === 0 ? (
					<div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-4 py-8 text-center">
						<p className="text-sm text-muted-foreground">
							No abbreviations yet. Add your first abbreviation above to speed up
							narrative writing.
						</p>
					</div>
				) : filtered.length === 0 && searchQuery.trim() ? (
					<div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-4 py-6 text-center">
						<p className="text-sm text-muted-foreground">
							No abbreviations match &lsquo;{searchQuery}&rsquo;
						</p>
					</div>
				) : (
					<div className="flex flex-col h-full rounded-md border border-border/50">
						<div className="flex-1 min-h-0 overflow-y-auto">
							{filtered.map((a) => (
								<AbbreviationRow
									key={a.id}
									id={a.id}
									abbreviation={a.abbreviation}
									expansion={a.expansion}
									onUpdate={handleUpdate}
									onDelete={handleDelete}
									searchQuery={searchQuery.trim() || undefined}
									highlight={a.id === highlightId}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
